import { useNavigate } from 'react-router';
import { formatInteger } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Avatar, Banner, Button, Pill, Skeleton } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { useDealAppointments } from '../api';
import type { DealRecord, UserRef } from '../types';
import { PropertyCard, SideCard } from './bits';
import styles from './record.module.css';

/** Deal-Akte 03 „Kontakt & Termine": the contact card with its actions, the appointment table, „Zuordnung". */
export function ContactTab({ deal }: { deal: DealRecord }) {
  const navigate = useNavigate();
  const appointments = useDealAppointments(deal.id, true);
  const contact = deal.contact;
  const meta = [contact.lifecycleLabel, contact.phone, contact.email, contact.city]
    .filter(Boolean)
    .join(' · ');

  const person = (user: UserRef | null) =>
    user ? (
      <span className={styles.gridPerson}>
        <Avatar initials={user.initials} name={user.name} size={26} tone="sand" />
        <span className={styles.gridCell}>{user.shortName}</span>
      </span>
    ) : (
      <span className={styles.gridMuted}>–</span>
    );

  const columns = '40px minmax(0,1.2fr) 150px 150px 150px 130px';

  return (
    <div className={styles.content}>
      <div className={styles.main}>
        <section className={styles.card} aria-label="Kontakt">
          <div className={styles.contactRow}>
            <Avatar initials={contact.initials} name={contact.name} size={40} tone="sand" />
            <div className={styles.contactText}>
              <div className={styles.contactName}>{contact.name}</div>
              <div className={styles.contactMeta}>{meta || '—'}</div>
            </div>
            <div className={styles.contactActions}>
              <Button
                variant="secondary"
                size="small"
                onClick={() => void navigate(`${contact.href}?editor=call`)}
              >
                Anrufen
              </Button>
              <Button
                variant="secondary"
                size="small"
                onClick={() => void navigate(`${contact.href}?editor=email`)}
              >
                E-Mail
              </Button>
              <Button
                variant="secondary"
                size="small"
                onClick={() => void navigate(`${contact.href}?editor=whatsapp`)}
              >
                WhatsApp
              </Button>
              <Button variant="secondary" size="small" onClick={() => void navigate(contact.href)}>
                Kontaktakte
              </Button>
            </div>
          </div>
        </section>
        <section className={styles.card} aria-label="Termine">
          <div className={styles.cardTitleRow}>
            <div>
              <div className={styles.cardTitle}>Termine</div>
              <p className={styles.cardIntro}>
                Setter und Closer werden getrennt geführt. Jede Buchung und Umbuchung ist ein eigener Eintrag.
              </p>
            </div>
            <Button
              variant="secondary"
              size="small"
              onClick={() => void navigate(`${contact.href}?editor=appointment`)}
            >
              Termin buchen
            </Button>
          </div>
          <div style={{ marginTop: 16 }}>
            <div className={clsx(styles.grid, styles.gridHead)} style={{ gridTemplateColumns: columns }}>
              <span>#</span>
              <span>Termin</span>
              <span>Datum</span>
              <span>Gebucht von</span>
              <span>Durchgeführt</span>
              <span>Status</span>
            </div>
            {appointments.isPending ? (
              <div className={styles.skeletonStack} style={{ paddingTop: 8 }}>
                <Skeleton variant="block" height={36} />
                <Skeleton variant="block" height={36} />
              </div>
            ) : null}
            {appointments.isError ? <Banner tone="danger">{errorMessage(appointments.error)}</Banner> : null}
            {appointments.data?.items.length === 0 ? (
              <div className={styles.grid} style={{ gridTemplateColumns: 'minmax(0,1fr)' }}>
                <span className={styles.gridMuted}>Noch kein Termin.</span>
              </div>
            ) : null}
            {appointments.data?.items.map((item) => (
              <button
                key={item.id}
                type="button"
                className={clsx(styles.grid, styles.taskRow)}
                style={{ gridTemplateColumns: columns, display: 'grid' }}
                onClick={() => void navigate(`/calendar/appointments/${item.id}`)}
              >
                <span className={styles.gridMuted}>#{formatInteger(item.number)}</span>
                <span className={clsx(styles.gridCell, styles.gridStrong)}>{item.typeName}</span>
                <span className={clsx(styles.gridCell, styles.gridMuted)}>{item.dateLabel}</span>
                {person(item.bookedBy)}
                {person(item.heldBy)}
                <span>
                  <Pill variant={item.tone}>{item.statusLabel}</Pill>
                </span>
              </button>
            ))}
          </div>
        </section>
      </div>
      <div className={styles.side}>
        <SideCard label="Zuordnung">
          <div className={styles.assignment}>
            <div className={styles.assignmentRow}>
              <span className={styles.assignmentKey}>Setter</span>
              <span className={styles.assignmentValue}>{appointments.data?.assignment.setter ?? '–'}</span>
            </div>
            <div className={styles.assignmentRow}>
              <span className={styles.assignmentKey}>Closer</span>
              <span className={styles.assignmentValue}>{appointments.data?.assignment.closer ?? '–'}</span>
            </div>
            <div className={styles.assignmentRow}>
              <span className={styles.assignmentKey}>Tippgeber</span>
              <span className={styles.assignmentValue}>{appointments.data?.assignment.referrer ?? '–'}</span>
            </div>
            {appointments.data?.assignment.referrerPartner ? (
              <div className={styles.assignmentRow}>
                <span className={styles.assignmentKey}>Tippgeber-Partner</span>
                <span className={styles.assignmentValue}>{appointments.data.assignment.referrerPartner}</span>
              </div>
            ) : null}
            <div className={styles.assignmentRow}>
              <span className={styles.assignmentKey}>No-Shows</span>
              <span className={styles.assignmentValue}>
                {appointments.data ? formatInteger(appointments.data.assignment.noShows) : '–'}
              </span>
            </div>
          </div>
          <p className={styles.footnote}>
            Der erste gebuchte Sales-Call hat diesen Deal erzeugt. Setter erhalten in V1 keine Provision.
          </p>
        </SideCard>
        <PropertyCard deal={deal} />
      </div>
    </div>
  );
}
